import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pulse, Play, Pause, Warning, ShieldCheck, WifiHigh, ArrowsClockwise } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";

const BUFFER = 40;
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const rand = (a, b) => a + Math.random() * (b - a);

// ---- live simulator ---------------------------------------------------------
const makeInitial = () => ({
  supply: 128, ret: 71, static: 0.48, current: 7.4, inducer: 3400, co: 18, flame: 4.6, health: 98,
});

function useTelemetry(running, scenario) {
  const [data, setData] = useState(makeInitial);
  const [history, setHistory] = useState({ supply: [], static: [], co: [] });
  const [faults, setFaults] = useState([]);
  const state = useRef(makeInitial());
  const alertFired = useRef(false);

  const step = useCallback(() => {
    const s = state.current;
    const alert = scenario === "alert";
    // drift with gentle noise
    s.supply = clamp(s.supply + rand(-1.6, 1.6) + (alert ? 0.6 : 0), 108, 150);
    s.ret = clamp(s.ret + rand(-0.5, 0.5), 66, 76);
    s.static = clamp(s.static + rand(-0.015, 0.015) + (alert ? 0.004 : 0), 0.35, 0.72);
    s.current = clamp(s.current + rand(-0.25, 0.25), 5.5, 9.5);
    s.inducer = clamp(s.inducer + rand(-45, 45), 3100, 3700);
    if (alert) {
      s.co = clamp(s.co + rand(6, 22), 18, 260);
      s.flame = clamp(s.flame - rand(0.05, 0.2), 0.3, 5);
      s.health = clamp(s.health - rand(1.4, 3.2), 34, 100);
    } else {
      s.co = clamp(s.co + rand(-4, 4), 4, 46);
      s.flame = clamp(s.flame + rand(-0.15, 0.15), 3.6, 5.6);
      s.health = clamp(s.health + rand(-0.6, 1.2), 90, 100);
    }
    setData({ ...s });
    setHistory((h) => ({
      supply: [...h.supply, s.supply].slice(-BUFFER),
      static: [...h.static, s.static].slice(-BUFFER),
      co: [...h.co, s.co].slice(-BUFFER),
    }));

    const now = new Date();
    const ts = now.toLocaleTimeString("en-US", { hour12: false });
    if (alert && !alertFired.current) {
      alertFired.current = true;
      setFaults((f) => [
        { id: Date.now(), ts, code: "E3", msg: "IGNITION LOCKOUT — flame signal low", level: "crit" },
        { id: Date.now() + 1, ts, code: "W7", msg: "CO trend rising above threshold", level: "warn" },
        ...f,
      ].slice(0, 6));
    }
    if (!alert && alertFired.current) {
      alertFired.current = false;
      setFaults((f) => [{ id: Date.now(), ts, code: "OK", msg: "System recovered — nominal operation", level: "ok" }, ...f].slice(0, 6));
    }
  }, [scenario]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(step, 1100);
    return () => clearInterval(id);
  }, [running, step]);

  return { data, history, faults };
}

// ---- sparkline --------------------------------------------------------------
const Sparkline = ({ points, color, min, max, height = 44 }) => {
  if (!points.length) return <div style={{ height }} />;
  const w = 100;
  const norm = (v) => height - ((clamp(v, min, max) - min) / (max - min)) * height;
  const dx = w / Math.max(points.length - 1, 1);
  const d = points.map((p, i) => `${i * dx},${norm(p).toFixed(1)}`).join(" ");
  const last = points[points.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className="w-full" style={{ height }}>
      <polyline points={d} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      <circle cx={(points.length - 1) * dx} cy={norm(last)} r="2" fill={color} />
    </svg>
  );
};

const Metric = ({ label, value, unit, spark, color, danger }) => (
  <div className={`border ${danger ? "border-brand-ember/40" : "border-white/10"} bg-ink p-4 transition-colors`}>
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-widest text-slatex-500">{label}</span>
      {danger && <Warning size={12} weight="fill" className="text-brand-ember" />}
    </div>
    <div className="mt-1 flex items-baseline gap-1">
      <span className={`font-display text-2xl font-extrabold tabular-nums ${danger ? "text-brand-ember" : "text-white"}`}>{value}</span>
      <span className="font-mono text-[10px] text-slatex-500">{unit}</span>
    </div>
    <div className="mt-2">{spark}</div>
  </div>
);

export const FurnaceSentinelDemo = () => {
  const [running, setRunning] = useState(true);
  const [scenario, setScenario] = useState("normal");
  const { data, history, faults } = useTelemetry(running, scenario);
  const tempRise = Math.round(data.supply - data.ret);
  const alert = scenario === "alert";
  const cyan = "#00F0FF";
  const ember = "#E63946";

  return (
    <section id="furnace-sentinel" data-testid="furnace-sentinel-demo" className="relative overflow-hidden border-t border-white/10 bg-ink py-24 md:py-32">
      <div className="dot-grid absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
        <Reveal className="mb-12 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-brand-cyan" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-cyan">Furnace Sentinel · Live Demo</span>
            </div>
            <h2 className="max-w-2xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight md:text-5xl">
              Watch a system report on itself.
            </h2>
            <p className="mt-5 max-w-xl font-body text-base leading-relaxed text-slatex-300">
              A simulated view of the telemetry Furnace Sentinel monitors continuously. Trigger a fault to see how predictive monitoring catches trouble before a no-heat call.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="sentinel-scenario-toggle" onClick={() => setScenario((s) => (s === "normal" ? "alert" : "normal"))}
              className={`flex items-center gap-2 border px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${alert ? "border-brand-ember bg-brand-ember/15 text-brand-ember" : "border-white/15 text-slatex-300 hover:border-white/40"}`}>
              <Warning size={13} weight="fill" /> {alert ? "Clear Fault" : "Trigger Fault"}
            </button>
            <button data-testid="sentinel-play-toggle" onClick={() => setRunning((r) => !r)}
              className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest text-slatex-300 transition-colors hover:border-white/40">
              {running ? <Pause size={13} weight="fill" /> : <Play size={13} weight="fill" />} {running ? "Pause" : "Resume"}
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className={`border ${alert ? "border-brand-ember/40" : "border-white/12"} bg-panel/60 backdrop-blur-sm transition-colors`}>
            {/* header bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-5 py-3">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center border border-brand-cyan/40 bg-brand-cyan/10">
                  <Pulse size={15} weight="duotone" className="text-brand-cyan" />
                </span>
                <span className="font-display text-sm font-bold tracking-tight">Furnace Sentinel</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-slatex-500">UNIT · GF-2140 · WEST LOOP</span>
              </div>
              <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-widest">
                <span className="flex items-center gap-1.5 text-slatex-300"><WifiHigh size={13} weight="bold" className="text-brand-cyan" /> Connected</span>
                <span className={`flex items-center gap-1.5 ${running ? "text-brand-cyan" : "text-slatex-500"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${running ? "bg-brand-cyan pulse-ring" : "bg-slatex-500"}`} /> {running ? "LIVE" : "PAUSED"}
                </span>
              </div>
            </div>

            <div className="grid gap-px bg-white/10 lg:grid-cols-[1fr_1.6fr_1fr]">
              {/* left: health ring */}
              <div className="flex flex-col items-center justify-center gap-4 bg-ink p-8">
                <span className="font-mono text-[10px] uppercase tracking-widest text-slatex-500">System Health</span>
                <div className="relative flex h-40 w-40 items-center justify-center">
                  <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                    <motion.circle cx="50" cy="50" r="44" fill="none" stroke={alert ? ember : cyan} strokeWidth="6" strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 44}
                      animate={{ strokeDashoffset: 2 * Math.PI * 44 * (1 - data.health / 100) }}
                      transition={{ duration: 0.8, ease: "easeOut" }} />
                  </svg>
                  <div className="absolute flex flex-col items-center">
                    <span className={`font-display text-4xl font-extrabold tabular-nums ${alert ? "text-brand-ember" : "text-white"}`}>{Math.round(data.health)}</span>
                    <span className="font-mono text-[10px] text-slatex-500">/ 100</span>
                  </div>
                </div>
                <div className={`flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-widest ${alert ? "border-brand-ember/50 text-brand-ember" : "border-brand-cyan/40 text-brand-cyan"}`}>
                  {alert ? <><Warning size={13} weight="fill" /> Fault Detected</> : <><ShieldCheck size={13} weight="fill" /> Nominal</>}
                </div>
              </div>

              {/* middle: metrics grid */}
              <div className="grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-3">
                <Metric label="Supply Temp" value={Math.round(data.supply)} unit="°F" color={cyan}
                  spark={<Sparkline points={history.supply} color={cyan} min={108} max={150} />} />
                <Metric label="Temp Rise" value={tempRise} unit="°F" danger={tempRise > 70}
                  spark={<div className="h-11 flex items-end"><div className="w-full bg-white/5"><div className="h-1 bg-brand-cyan" style={{ width: `${clamp((tempRise / 90) * 100, 4, 100)}%` }} /></div></div>} />
                <Metric label="Static Press." value={data.static.toFixed(2)} unit="in WC" color={cyan}
                  spark={<Sparkline points={history.static} color={cyan} min={0.35} max={0.72} />} />
                <Metric label="Blower Current" value={data.current.toFixed(1)} unit="A" color={cyan}
                  spark={<div className="h-11 flex items-end"><div className="w-full bg-white/5"><div className="h-1 bg-brand-cyan" style={{ width: `${clamp((data.current / 12) * 100, 4, 100)}%` }} /></div></div>} />
                <Metric label="Inducer" value={Math.round(data.inducer)} unit="RPM" color={cyan}
                  spark={<div className="h-11 flex items-end"><div className="w-full bg-white/5"><div className="h-1 bg-brand-cyan" style={{ width: `${clamp(((data.inducer - 3000) / 800) * 100, 4, 100)}%` }} /></div></div>} />
                <Metric label="CO Level" value={Math.round(data.co)} unit="ppm" danger={data.co > 70} color={data.co > 70 ? ember : cyan}
                  spark={<Sparkline points={history.co} color={data.co > 70 ? ember : cyan} min={0} max={260} />} />
              </div>

              {/* right: fault stream */}
              <div className="flex flex-col bg-ink p-5">
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slatex-500">Event Log</span>
                  <ArrowsClockwise size={12} className={`text-slatex-500 ${running ? "animate-spin" : ""}`} style={{ animationDuration: "3s" }} />
                </div>
                <div className="flex-1 space-y-2 overflow-hidden">
                  <AnimatePresence initial={false}>
                    {faults.length === 0 && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono text-[11px] text-slatex-500">
                        No active faults. Monitoring…
                      </motion.div>
                    )}
                    {faults.map((f) => (
                      <motion.div key={f.id} layout initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                        className={`flex items-start gap-2 border-l-2 bg-panel/60 py-2 pl-3 pr-2 ${f.level === "crit" ? "border-brand-ember" : f.level === "warn" ? "border-yellow-500" : "border-brand-cyan"}`}>
                        <span className={`font-mono text-[10px] font-bold ${f.level === "crit" ? "text-brand-ember" : f.level === "warn" ? "text-yellow-400" : "text-brand-cyan"}`}>{f.code}</span>
                        <div className="min-w-0">
                          <div className="truncate font-body text-[12px] text-white">{f.msg}</div>
                          <div className="font-mono text-[9px] text-slatex-500">{f.ts}</div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
                <div className="mt-4 border-t border-white/10 pt-3 font-mono text-[9px] uppercase tracking-widest text-slatex-500">
                  Flame signal · <span className={data.flame < 2 ? "text-brand-ember" : "text-brand-cyan"}>{data.flame.toFixed(1)} µA</span>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slatex-500">
            * Simulated demonstration data. Furnace Sentinel monitoring is under active development.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default FurnaceSentinelDemo;
